/**
 * Shared input validators used across tenant flows (e.g. staff user forms).
 */

/**
 * Practical email check — rejects obviously invalid shapes without claiming full RFC 5322.
 * @param {string} email
 * @returns {boolean}
 */
export function isValidEmail(email) {
  const s = String(email ?? "").trim();
  if (!s || s.length > 254) return false;
  const [local, ...rest] = s.split("@");
  if (rest.length !== 1 || !local || local.length > 64) return false;
  const domain = rest[0];
  if (!domain || domain.length > 253 || domain.endsWith(".")) return false;
  if (local.includes("..")) return false;
  const re =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
  if (!re.test(s)) return false;
  const tld = domain.split(".").pop();
  return Boolean(tld && tld.length >= 2 && /^[a-zA-Z]+$/.test(tld));
}

/**
 * Strip formatting and return Saudi mobile subscriber digits (9 digits, starting with 5).
 * Accepts: +966 5XX XXX XXXX, 9665XXXXXXXX, 05XXXXXXXX, 5XXXXXXXX (already national mobile).
 * Does not validate landlines (start with 1 after stripping 0).
 * @param {string} input
 * @returns {string|null}
 */
export function normalizeKsaMobile(input) {
  let s = String(input ?? "")
    .replace(/[\s().-]/g, "")
    .trim();
  if (!s) return null;
  if (s.startsWith("+")) s = s.slice(1);
  if (s.startsWith("00")) s = s.slice(2);
  if (s.startsWith("966")) s = s.slice(3);
  if (s.startsWith("0")) s = s.slice(1);
  if (/^5\d{8}$/.test(s)) return s;
  return null;
}

/**
 * @param {string} input
 * @returns {boolean}
 */
export function isValidKsaPhone(input) {
  return normalizeKsaMobile(input) !== null;
}

/**
 * Canonical E.164-style storage for Saudi mobiles (+966 + 9 digits).
 * @param {string} nineDigits — output of {@link normalizeKsaMobile}
 * @returns {string}
 */
export function formatKsaE164(nineDigits) {
  if (!nineDigits || !/^5\d{8}$/.test(nineDigits)) return "";
  return `+966${nineDigits}`;
}

const ARABIC_INDIC_DIGIT_MAP = {
  "٠": "0",
  "١": "1",
  "٢": "2",
  "٣": "3",
  "٤": "4",
  "٥": "5",
  "٦": "6",
  "٧": "7",
  "٨": "8",
  "٩": "9",
};

/** Matches Chefz menu sync rule: 9+ digits, optional +/00 prefix, spaces/dashes. */
const PHONE_LIKE_DIGIT_RE = /(?:\+|00)?(?:\d[\s-]*){8,}\d/;

/**
 * Detect phone-like number sequences in free text (product/category name or description).
 * @param {string} text
 * @returns {boolean}
 */
export function containsPhoneLikeNumber(text) {
  if (!text) return false;
  const normalized = String(text).replace(
    /[٠-٩]/g,
    (d) => ARABIC_INDIC_DIGIT_MAP[d] || d
  );
  return PHONE_LIKE_DIGIT_RE.test(normalized);
}

/**
 * @param {string} text
 * @param {(key: string) => string} translate — e.g. i18n `t`
 * @returns {string} error message or empty string
 */
export function getPhoneLikeNumberError(text, translate) {
  return containsPhoneLikeNumber(text)
    ? translate("Using Phone numbers are not allowed.")
    : "";
}
